import { useEffect, useRef, useState } from 'react';
import fleetApi from '../../services/api.service';
import { buildUrlWithParams } from '../../utils/url.utils';

const PAGE_SIZE = 10;

/**
 * Historique d'un envoi AS400 (tentatives, relances, modifications), du plus récent au plus ancien.
 * Chargé uniquement si enabled (réservé aux admins). fetchNextPage charge les événements plus anciens.
 */
export default function useGetTourLogHistory(logId, enabled = true) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchPage = async (offset) => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setIsLoading(true);
      setError('');
      const response = await fleetApi.get(
        buildUrlWithParams(`/tours/tours-logs/${logId}/history`, {
          limit: PAGE_SIZE,
          offset,
          orderBy: 'DESC',
        }),
        { signal: abortController.signal }
      );
      setItems((prevItems) =>
        offset === 0
          ? response.data.items
          : [
              ...prevItems,
              // Un événement ajouté entre deux pages décale l'offset : pas de doublon
              ...response.data.items.filter(
                (item) => !prevItems.some((prev) => prev.id === item.id)
              ),
            ]
      );
      setTotal(response.data.pagination_info?.total_items || 0);
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      setError(
        error?.response?.data?.message || 'Impossible de charger l’historique.'
      );
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const reload = () => {
    if (logId && enabled) {
      fetchPage(0);
    }
  };

  useEffect(() => {
    setItems([]);
    setTotal(0);
    setError('');
    reload();
    return () => abortControllerRef.current?.abort();
  }, [logId, enabled]);

  return {
    items,
    total,
    error,
    isLoading,
    hasMore: items.length < total,
    fetchNextPage: () => fetchPage(items.length),
    reload,
  };
}
